import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Lock, Heart, ArrowRight, Loader2, AlertCircle, User, CheckCircle2 } from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import { useNavigate } from 'react-router-dom';

const AuthScreen: React.FC = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const isPasswordMatch = useMemo(() => {
    if (isLogin) return true;
    if (!password || !passwordConfirm) return false;
    return password === passwordConfirm;
  }, [isLogin, password, passwordConfirm]);

  const canSubmit = useMemo(() => {
    if (isLogin) return email && password;
    return username && email && password && passwordConfirm && isPasswordMatch;
  }, [isLogin, username, email, password, passwordConfirm, isPasswordMatch]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    
    setLoading(true);
    setError(null);

    try {
      if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
        navigate('/');
      } else {
        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              username: username,
            }
          }
        });
        if (signUpError) throw signUpError;

        if (signUpData.user) {
          // Explicitly create profile entry
          const { error: profileError } = await supabase
            .from('profiles')
            .insert([{ 
              id: signUpData.user.id, 
              couple_id: signUpData.user.id 
            }]);
          
          if (profileError) {
            console.error('Profile creation error during signup:', profileError);
            // We don't necessarily throw here if we want to allow login, 
            // but the user requested error handling.
          }
        }

        alert('회원가입이 완료되었습니다. 이메일을 확인하거나 로그인해 주세요.');
        setIsLogin(true);
      }
    } catch (err: any) {
      setError(err.message || '인증에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 py-12">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-10">
          <div className="bg-primary/10 w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-sm">
            <Heart className="text-primary fill-primary" size={40} />
          </div>
          <h1 className="text-3xl font-black text-charcoal tracking-tight">Our Moment</h1>
          <p className="text-gray-400 mt-2 font-medium">우리만의 소중한 기록을 시작하세요</p>
        </div>

        <div className="bg-white rounded-[40px] p-8 shadow-xl shadow-primary/5 border border-secondary/50">
          <div className="flex bg-secondary p-1 rounded-2xl mb-8">
            <button 
              onClick={() => {
                setIsLogin(true);
                setError(null);
              }}
              className={`flex-1 py-3 text-sm font-bold rounded-xl transition-all ${isLogin ? 'bg-white text-primary shadow-sm' : 'text-gray-400'}`}
            >
              로그인
            </button>
            <button 
              onClick={() => {
                setIsLogin(false);
                setError(null);
              }}
              className={`flex-1 py-3 text-sm font-bold rounded-xl transition-all ${!isLogin ? 'bg-white text-primary shadow-sm' : 'text-gray-400'}`}
            >
              회원가입
            </button>
          </div>

          <form onSubmit={handleAuth} className="space-y-5">
            <AnimatePresence mode="wait">
              {error && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="bg-red-50 text-red-500 p-4 rounded-2xl flex items-start gap-3 text-xs font-bold border border-red-100"
                >
                  <AlertCircle size={16} className="shrink-0" />
                  <p>{error}</p>
                </motion.div>
              )}
            </AnimatePresence>

            {!isLogin && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <label className="block text-[10px] font-black text-gray-400 mb-2 ml-1 uppercase tracking-wider">아이디 (사용자 이름)</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
                  <input 
                    type="text" 
                    required={!isLogin}
                    placeholder="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full bg-secondary/50 border-none rounded-2xl pl-12 pr-4 py-4 text-sm focus:ring-2 focus:ring-primary outline-none transition-all font-bold"
                  />
                </div>
              </motion.div>
            )}

            <div>
              <label className="block text-[10px] font-black text-gray-400 mb-2 ml-1 uppercase tracking-wider">이메일 주소</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
                <input 
                  type="email" 
                  required
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-secondary/50 border-none rounded-2xl pl-12 pr-4 py-4 text-sm focus:ring-2 focus:ring-primary outline-none transition-all font-bold"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5">
              <div>
                <label className="block text-[10px] font-black text-gray-400 mb-2 ml-1 uppercase tracking-wider">비밀번호</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
                  <input 
                    type="password" 
                    required
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-secondary/50 border-none rounded-2xl pl-12 pr-4 py-4 text-sm focus:ring-2 focus:ring-primary outline-none transition-all font-bold"
                  />
                </div>
              </div>

              {!isLogin && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="flex justify-between items-center mb-2 ml-1">
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-wider">비밀번호 확인</label>
                    {passwordConfirm && (
                      <span className={`text-[10px] font-black flex items-center gap-1 ${isPasswordMatch ? 'text-green-500' : 'text-red-500'}`}>
                        {isPasswordMatch ? <><CheckCircle2 size={10} /> 일치함</> : '일치하지 않음'}
                      </span>
                    )}
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
                    <input 
                      type="password" 
                      required={!isLogin}
                      placeholder="••••••••"
                      value={passwordConfirm}
                      onChange={(e) => setPasswordConfirm(e.target.value)}
                      className={`w-full bg-secondary/50 border-none rounded-2xl pl-12 pr-4 py-4 text-sm focus:ring-2 outline-none transition-all font-bold ${passwordConfirm ? (isPasswordMatch ? 'focus:ring-green-500' : 'focus:ring-red-500') : 'focus:ring-primary'}`}
                    />
                  </div>
                </motion.div>
              )}
            </div>

            <button 
              type="submit" 
              disabled={loading || !canSubmit}
              className="w-full bg-primary text-white font-black py-5 rounded-2xl shadow-xl shadow-primary/30 active:scale-[0.98] transition-all disabled:opacity-50 disabled:grayscale disabled:scale-100 flex items-center justify-center gap-2 mt-4"
            >
              {loading ? (
                <Loader2 className="animate-spin" size={20} />
              ) : (
                <>
                  <span>{isLogin ? '시작하기' : '가입하기'}</span>
                  <ArrowRight size={20} />
                </>
              )}
            </button>
          </form>
        </div>

        <p className="text-center mt-10 text-[11px] text-gray-300 font-bold">
          © 2026 Our Moment. All rights reserved.
        </p>
      </motion.div>
    </div>
  );
};

export default AuthScreen;
